import { useMemo } from "react";

import type { HotelPolicyQuery } from "@/lib/gql/__generated__/graphql";

import { useSendTestEmailRequestReservationConfirmed } from "@/components/features/items/edit/_shared/repository/mutation/send-sample-email/request-reservation/confirmed";
import { useSendTestEmailRequestReservationDeclined } from "@/components/features/items/edit/_shared/repository/mutation/send-sample-email/request-reservation/declined";
import { useSendTestEmailRequestReservationRequested } from "@/components/features/items/edit/_shared/repository/mutation/send-sample-email/request-reservation/requested";
import { useSendTestEmailReservationCin } from "@/components/features/items/edit/_shared/repository/mutation/send-sample-email/reservation/c-in";
import { useSendTestEmailReservationCanceled } from "@/components/features/items/edit/_shared/repository/mutation/send-sample-email/reservation/canceled";
import { useSendTestEmailReservationCreated } from "@/components/features/items/edit/_shared/repository/mutation/send-sample-email/reservation/created";
import { useSendTestEmailReservationUpdated } from "@/components/features/items/edit/_shared/repository/mutation/send-sample-email/reservation/updated";

import { useInitAccommodationHotelPolicyFormModelContext } from "../../init";
import { useUpdateHotelPolicy } from "../use-update-hotel-policy";
import { serverModelToInputModelConverter } from "./converter/server-model-to-input-model";

import type { QueryData } from "../../type";

type Props = {
	hotelID: string;
	serverModel: HotelPolicyQuery;
};

export const useInitialized = ({ hotelID, serverModel }: Props) => {
	const inputModel = useMemo(() => serverModelToInputModelConverter({ serverModel }), [serverModel]);

	const queryData: QueryData = {
		hotel: {
			hotelEmailAddress: serverModel?.hotel.email ?? "",
		},
		planList: serverModel?.planList.plans,
		hotelDisplaySettings: {
			id: serverModel.hotelDisplaySettings.id,
			hotelID: serverModel.hotelDisplaySettings.hotelID,
		},
		isCreditCardRegistered: serverModel?.hotelApplicationStatus?.isCreditCardRegistered ?? false,
	} satisfies QueryData;

	const { viewModel, onSetViewModel, onResetViewModel } = useInitAccommodationHotelPolicyFormModelContext({
		inputModel,
		queryData,
	});

	const { onUpdateHotelPolicy: onUpdate } = useUpdateHotelPolicy({
		hotelID,
		hotelDisplaySettingsId: serverModel.hotelDisplaySettings.id,
		viewModel,
		onSuccessCallback: onResetViewModel,
	});

	const { onSendTestEmailReservationCin } = useSendTestEmailReservationCin();
	const { onSendTestEmailReservationCanceled } = useSendTestEmailReservationCanceled();
	const { onSendTestEmailReservationCreated } = useSendTestEmailReservationCreated();
	const { onSendTestEmailReservationUpdated } = useSendTestEmailReservationUpdated();

	const { onSendTestEmailRequestReservationConfirmed } = useSendTestEmailRequestReservationConfirmed();
	const { onSendTestEmailRequestReservationDeclined } = useSendTestEmailRequestReservationDeclined();
	const { onSendTestEmailRequestReservationRequested } = useSendTestEmailRequestReservationRequested();

	return {
		queryData,
		viewModel,
		onSetViewModel,
		onUpdate,

		testEmailSender: {
			onSendTestEmailReservationCin,
			onSendTestEmailReservationCanceled,
			onSendTestEmailReservationCreated,
			onSendTestEmailReservationUpdated,

			onSendTestEmailRequestReservationConfirmed,
			onSendTestEmailRequestReservationDeclined,
			onSendTestEmailRequestReservationRequested,
		},
	};
};
